import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import useSwr from '@hooks/useSwr';
import StepsModal from '@components/StepsModal';
import api from '@services/api';
import CustomerStep from '@components/CustomerStep';
import moment from 'moment';
import { UnitType } from '@pages/Financial/ContractsMoT/NewContractModal/NewContractSteps/ConfigStep';
import { formatPricePerMB } from '@utils/formatters';
import ConfigStep, { IConfigStepValues } from './NewRequestSteps/ConfigStep';
import ContractStep, {
  IContractStepValues,
} from './NewRequestSteps/ContractStep';

type Flags = 'S' | 'N';
interface INewRequestModal {
  visible: boolean;
  onClose: Function;
  newRequestMutate: Function;
  defaultCustomerId?: number;
  defaultContractId?: number;
  showContracted?: boolean;
}

const NewRequestModal: React.FC<INewRequestModal> = ({
  visible,
  onClose,
  newRequestMutate,
  defaultCustomerId,
  defaultContractId,
  showContracted,
}) => {
  const { t } = useTranslation(['requests', 'attributes', 'contracts']);

  const [confirmLoading, setConfirmLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<number>();
  const [contractValues, setContractValues] = useState<IContractStepValues>();
  const [requestConfigValues, setRequestConfigValues] =
    useState<IConfigStepValues>();
  const [payPerUseFlag, setPayPerUseFlag] = useState<Flags>();
  const [operatorDiffChargeFlag, setOperatorDiffChargeFlag] = useState<Flags>();
  const [execPriceUnit, setExecPriceUnit] = useState<UnitType>('MB');

  const { data: contractsByClient } = useSwr(
    selectedCustomer && !defaultContractId
      ? `/service-proxy/broker/${selectedCustomer}/virtual-plan`
      : null,
  );

  const { data: contractData } = useSwr(
    defaultContractId && visible
      ? `/service-proxy/broker/virtual-plan/${defaultContractId}?showContracted=${showContracted}`
      : null,
  );

  const contractDetails = useMemo(() => {
    if (contractsByClient !== '' && contractsByClient) {
      return contractsByClient?.find(
        (contract: any) => contract.id === contractValues?.virtualPlanId,
      );
    }
    if (contractData) {
      return contractData?.virtualContract;
    }
    return undefined;
  }, [contractsByClient, contractData, contractValues?.virtualPlanId]);

  useEffect(() => {
    if (contractDetails) {
      setPayPerUseFlag(contractDetails?.payPerUseFlag);
      setOperatorDiffChargeFlag(contractDetails?.operatorDiffChargeFlag);
      setContractValues({
        virtualPlanId: contractDetails?.id,
      });

      setRequestConfigValues({
        price: contractDetails?.price,
        activationPrice: contractDetails?.activationPrice,
        megabyteExcPrice: contractDetails?.megabyteExcPrice,
        loyaltyType: contractDetails?.loyaltyType,
        loyaltyTime: contractDetails?.loyaltyTime,
        penalty: contractDetails?.penalty,
        sharingType: contractDetails?.sharingType,
        payPerUseFlag: contractDetails?.payPerUseFlag,
        automaticBlocking: contractDetails?.automaticBlocking,
        franchise: contractDetails?.franchise,
        motOperators: contractDetails?.motOperators,
        description: undefined,
        activationDate: undefined,
        nbPreActivationDays: undefined,
        minimumConsumption: 1,
        tradeInMonths: undefined,
        blockedByDefault: contractDetails?.franchise === 0,
        chargeIscaOnTransfer: contractDetails?.chargeIscaOnTransfer || false,
        iscaDurationInDays: contractDetails?.iscaDurationInDays || undefined,
        iscaMaxRenewal: contractDetails?.iscaMaxRenewal,
        planType: contractDetails?.planType,
        automaticRenewal: contractDetails?.automaticRenewal,
        planExpirationMonths: contractDetails?.planExpirationMonths,
      });
    }
  }, [contractDetails, contractData]);

  const createAccount = useCallback(
    (payload: any) =>
      api.post('/service-proxy/broker/virtual-account', payload),
    [],
  );

  const newRequestSteps = useMemo(
    () => [
      {
        title: t('attributes:customer'),
        doNotDisplay: defaultCustomerId,
        disabled:
          (!selectedCustomer && !defaultCustomerId) || !contractsByClient,
        loading:
          !!selectedCustomer && !contractsByClient && contractsByClient !== '',
        content: (
          <CustomerStep
            value={selectedCustomer}
            onChange={customerId => {
              setContractValues(undefined);
              setSelectedCustomer(customerId);
            }}
            showMessage={contractsByClient === ''}
          />
        ),
      },
      {
        title: t('attributes:contract'),
        doNotDisplay: defaultContractId,
        disabled:
          (!contractValues?.virtualPlanId && !defaultContractId) ||
          (contractDetails?.loyaltyType === 2 && !contractDetails?.loyaltyTime),
        loading: !!contractValues?.virtualPlanId && !contractValues,
        content: (
          <ContractStep
            contractDetails={contractValues}
            onChange={setContractValues}
            defaultContractId={defaultContractId}
            contractsByClient={contractsByClient}
          />
        ),
      },
      {
        title: t('modal.stepsTitles.config'),
        disabled:
          !requestConfigValues?.description ||
          requestConfigValues?.description === '',
        content: (
          <ConfigStep
            onChange={setRequestConfigValues}
            payPerUseFlag={payPerUseFlag}
            isLoading={!contractValues?.virtualPlanId && !contractDetails}
            operatorDiffChargeFlag={operatorDiffChargeFlag}
            contractDetails={
              defaultCustomerId
                ? contractData?.virtualContract
                : contractDetails
            }
            execPriceUnit={execPriceUnit}
            setExecPriceUnit={setExecPriceUnit}
          />
        ),
      },
    ],
    [
      t,
      defaultCustomerId,
      selectedCustomer,
      contractsByClient,
      defaultContractId,
      contractValues,
      contractDetails,
      requestConfigValues?.description,
      payPerUseFlag,
      operatorDiffChargeFlag,
      contractData?.virtualContract,
      execPriceUnit,
    ],
  );

  const payload = useMemo(
    () => ({
      ppOperadoraInfos: [],
      overflowRanges: [],
      virtualPlanId: contractValues?.virtualPlanId || defaultContractId,
      price: requestConfigValues?.price,
      activationPrice: requestConfigValues?.activationPrice,
      megabyteExcPrice:
        formatPricePerMB(
          execPriceUnit,
          requestConfigValues?.megabyteExcPrice,
        ) ?? 0,
      loyaltyType:
        requestConfigValues?.loyaltyType || contractDetails?.loyaltyType,
      loyaltyTime: requestConfigValues?.loyaltyTime,
      penalty: requestConfigValues?.penalty || 0,
      automaticBlocking: requestConfigValues?.automaticBlocking,
      preActivationExemptTraffic: requestConfigValues?.minimumConsumption,
      nbPreActivationDays: requestConfigValues?.nbPreActivationDays ?? 0,
      name: requestConfigValues?.description,
      activationDate:
        requestConfigValues?.activationDate ?? moment().format('yyyy-MM-DD'),
      tradeInMonths: requestConfigValues?.tradeInMonths,
      blockedByDefault: requestConfigValues?.blockedByDefault,
      chargeIscaOnTransfer: requestConfigValues?.chargeIscaOnTransfer || false,
      iscaDurationInDays: requestConfigValues?.iscaDurationInDays,
      iscaMaxRenewal: requestConfigValues?.iscaMaxRenewal,
      planType: contractDetails?.planType,
      automaticRenewal: requestConfigValues?.automaticRenewal,
      planExpirationMonths: requestConfigValues?.planExpirationMonths,
    }),
    [
      contractValues?.virtualPlanId,
      defaultContractId,
      requestConfigValues?.price,
      requestConfigValues?.activationPrice,
      requestConfigValues?.megabyteExcPrice,
      requestConfigValues?.loyaltyType,
      requestConfigValues?.loyaltyTime,
      requestConfigValues?.penalty,
      requestConfigValues?.automaticBlocking,
      requestConfigValues?.minimumConsumption,
      requestConfigValues?.nbPreActivationDays,
      requestConfigValues?.description,
      requestConfigValues?.activationDate,
      requestConfigValues?.tradeInMonths,
      requestConfigValues?.blockedByDefault,
      requestConfigValues?.chargeIscaOnTransfer,
      requestConfigValues?.iscaDurationInDays,
      requestConfigValues?.iscaMaxRenewal,
      requestConfigValues?.automaticRenewal,
      requestConfigValues?.planExpirationMonths,
      execPriceUnit,
      contractDetails?.loyaltyType,
      contractDetails?.planType,
    ],
  );

  const handleConfirm = useCallback(async () => {
    try {
      setConfirmLoading(true);

      if (requestConfigValues?.loyaltyType !== 2) {
        delete payload.loyaltyTime;
      }

      await createAccount(payload).then(() => {
        newRequestMutate(payload);
        message.success(t('modal.notifications.success'));
      });
      onClose();
    } catch {
      message.error(t('modal.notifications.error'));
    }

    setConfirmLoading(false);
  }, [
    requestConfigValues?.loyaltyType,
    createAccount,
    newRequestMutate,
    onClose,
    payload,
    t,
  ]);

  return (
    <StepsModal
      title={t('modal.title')}
      width="700px"
      onConfirm={handleConfirm}
      isOpen={visible}
      allowStepsLine
      steps={newRequestSteps}
      onClose={() => onClose()}
      confirmLoading={confirmLoading}
      confirmDisabled={
        !requestConfigValues?.description ||
        requestConfigValues?.description === ''
      }
    />
  );
};

export default NewRequestModal;
